import { inject, Injectable } from '@angular/core';
import { AggregationColumnState, CellClassRules, ColDef, ColGroupDef, GridState, SortModelItem, ValueFormatterFunc, ValueGetterFunc } from 'ag-grid-enterprise';
import { PermissionService, SecurityConditionContext } from '../../../core/security';
import { SubscriberResponseDto } from '../../subscriber';
import { COLUMN_TYPE_FILTER_MAP, ColumnQueryState, ReportColumnConfig, ReportColumnDefinition, ReportColumnGroupDefinition, ReportColumnGroupKeys, ReportColumnKeys, SupportedColumnFilterType } from '../report-column';
import { LayoutDefinition, LayoutOptions } from '../reporting.model';
import { ColumnFilter, ColumnFilters, DateColumnFilter, FILTER_TYPE_TO_AG_FILTER_MAP, NumberColumnFilter, SetColumnFilter, TextColumnFilter } from './report-grid.model';
import { numericFormatting, type NumericFormatting } from './value-formatters';
import { valueGetters } from './value-getters';

@Injectable({
    providedIn: 'root'
})
export class ReportGridService {
    private permissionService = inject(PermissionService);

    private isSysAdmin = this.permissionService.hasPermission('sysAdmin');

    getAgGridColDefs(
        reportColumnDefinitions: ReportColumnDefinition[], 
        reportColumnGroupDefinitions: ReportColumnGroupDefinition[], 
        subscriber: SubscriberResponseDto | null, 
        includeAllColumns: boolean
    ): ColDef[] {
        /* Sort columns by Order. Columns without an order specified will go last in alphabetical order */
        const sortedDefs = reportColumnDefinitions.sort((a, b) => {
            const aOrder = a.order;
            const bOrder = b.order;

            if (aOrder != null && bOrder != null) return aOrder - bOrder;
            if (aOrder != null) return -1;
            if (bOrder != null) return 1;

            return a.header.localeCompare(b.header);
        });

        /* Build the SecurityConditionContext for the Report Specific Rules */
        const securityContext: SecurityConditionContext = {
            isAdeptEnabled: subscriber?.isAdeptEnabled,
            isAmsEnabled: subscriber?.isAmsEnabled,
            isBriteEnabled: subscriber?.isBriteEnabled
        };

        /* Remove any Column that should be included based on Security Rules */
        const filteredColDefs = this.isSysAdmin && includeAllColumns
        ? sortedDefs
        : sortedDefs.filter((col) => !col.securityRuleKeys || this.permissionService.hasAllPermissions(col.securityRuleKeys, securityContext));

        const finalColDefs: (ColDef | ColGroupDef)[] = [];

        let currentGroup: ColGroupDef | null = null;

        const columnGroupMap = new Map<ReportColumnGroupKeys, ReportColumnGroupDefinition>();
        reportColumnGroupDefinitions.forEach((d) => columnGroupMap.set(d.key as ReportColumnGroupKeys, d));

        filteredColDefs.forEach((col) => {
            /* If ColDef has no group, then just add it. */
            if (col.columnGroupKey == null) {
                /* Flush pending group */
                if (currentGroup != null) {
                    finalColDefs.push(currentGroup);
                    currentGroup = null;
                }

                finalColDefs.push(this.getAgGridColDef(col));

                return;
            }

            const columnGroup = columnGroupMap.get(col.columnGroupKey);

            /* No Current Group or the column is in a new group */
            if (!currentGroup || currentGroup.headerName !== columnGroup?.header) {
                /* Flush pending group */
                if (currentGroup) {
                    finalColDefs.push(currentGroup);
                }

                currentGroup = {
                    headerName: columnGroup?.header,
                    // groupId: columnGroup?.key,
                    children: [this.getAgGridColDef(col)]
                };
            } else {
                /* Column belongs in the Current Group */
                currentGroup.children.push(this.getAgGridColDef(col));
            }
        });

        /* Flush the final group after the loop.
             The last group isn't pushed automatically, so add it now if it exists. */
        if (currentGroup != null) {
            finalColDefs.push(currentGroup);
        }

        return finalColDefs;
    }

    private getAgGridColDef(columnDef: ReportColumnDefinition): ColDef {
        const defaultNumericFormatting = columnDef.valueFormatter === undefined
            ? this.getNumericFormatting(columnDef)
            : undefined;

        return {
            colId: columnDef.key,
            field: columnDef.field,
            headerName: columnDef.header,

            /* Presentation */
            type: columnDef.type,
            hide: columnDef.visible !== true,
            sort: columnDef.sortDirection ?? null,
            sortIndex: columnDef.sortIndex ?? null,
            columnGroupShow: columnDef.columnGroupShow,
            valueGetter: this.getValueGetter(columnDef),
            valueFormatter: this.getValueFormatter(columnDef, defaultNumericFormatting),
            pinned: columnDef.pinned ?? null,
            width: columnDef.width ?? undefined,
            cellClassRules: this.getCellClassRules(defaultNumericFormatting),

            /* Grouping / Pivoting */
            enableRowGroup: columnDef.enableRowGroup ?? true,
            rowGroup: columnDef.rowGroup,
            enablePivot: columnDef.enablePivot ?? false,
            pivot: columnDef.pivot ?? false,

            /* Filtering */
            filter: columnDef.filterType ? FILTER_TYPE_TO_AG_FILTER_MAP[columnDef.filterType] : undefined,
            filterParams: this.getFilterParams(columnDef),
            filterValueGetter: this.getFilterValueGetter(columnDef),

            /* Aggregation */
            enableValue: columnDef.enableValue,
            aggFunc: columnDef.aggFunc,
            initialAggFunc: columnDef.initialAggFunc ?? undefined
        };
    }

    private getCellClassRules(formatting: NumericFormatting | undefined): CellClassRules | undefined {
        if (!formatting) {
            return undefined;
        }

        return {
            'text-danger': params => formatting.displayAsNegative(params.value)
        };
    }

    private getFilterParams(columnDef: ReportColumnDefinition): ColDef['filterParams'] {
        if (columnDef.filterType !== 'set') {
            return columnDef.filterParams;
        }

        return {
            ...columnDef.filterParams,
            suppressClearModelOnRefreshValues: true
        };
    }

    private getValueGetter(columnDef: ReportColumnDefinition): ValueGetterFunc | undefined {
        const field = columnDef.field;

        if (!field) {
            return undefined;
        }

        /* Value Getter on the Column Definition */
        if (columnDef.valueGetter) {
            return columnDef.valueGetter;
        }

        /* Column Definition specified no Getter */
        if (columnDef.valueGetter === null) {
            return undefined;
        }

        const types = Array.isArray(columnDef.type) ? columnDef.type : [columnDef.type];

        /* Date */
        if (types.includes('date')) {
            return valueGetters.date(field);
        }

        /* Boolean */
        if (types.includes('boolean')) {
            return valueGetters.boolean(field);
        }

        /* DateTime */
        if (types.includes('dateTime')) {
            return valueGetters.dateTime(field);
        }

        /* Year */
        if (types.includes('dateYear')) {
            return valueGetters.dateYear(field);
        }

        /* Month */
        if (types.includes('dateMonth')) {
            return valueGetters.dateMonth(field);
        }

        return undefined;
    }

    private getNumericFormatting(columnDef: ReportColumnDefinition): NumericFormatting | undefined {
        const types = Array.isArray(columnDef.type) ? columnDef.type : [columnDef.type];

        if (types.includes('number')) {
            return numericFormatting.number(columnDef.decimalPlaces ?? 0);
        }

        if (types.includes('percentage')) {
            return numericFormatting.percentage(columnDef.decimalPlaces ?? 0);
        }

        if (types.includes('currency')) {
            return numericFormatting.currency(columnDef.decimalPlaces ?? 2);
        }

        return undefined;
    }

    private getValueFormatter(columnDef: ReportColumnDefinition, defaultNumericFormatting: NumericFormatting | undefined): ValueFormatterFunc | undefined {
        /* Value Formatter on the Column Definition */
        if (columnDef.valueFormatter) {
            return columnDef.valueFormatter;
        }

        /* Column Definition specified no Formatter */
        if (columnDef.valueFormatter === null) {
            return undefined;
        }

        return defaultNumericFormatting?.valueFormatter;
    }

    private getFilterValueGetter(columnDef: ReportColumnDefinition): ValueGetterFunc | undefined {
        const field = columnDef.field;
        if (!field) {
            return undefined;
        }

        /* Value Formatter on the Column Definition */
        if (columnDef.filterValueGetter) {
            return columnDef.filterValueGetter;
        }

        /* Column Definition specified no Formatter */
        if (columnDef.filterValueGetter === null) {
            return undefined;
        }

        const types = Array.isArray(columnDef.type) ? columnDef.type : [columnDef.type];

        /* Percentage */
        if (types.includes('percentage')) {
            return valueGetters.percentageFromFraction(field);
        }

        return undefined;
    }


    private FILTER_MODEL_RESOLVERS: Record<SupportedColumnFilterType, ResolverFn> = {
        'text':  this.textFilterResolver,
        'number': this.numberFilterResolver,
        'date': this.dateFilterResolver,
        'set': this.setFilterResolver,
    }

    private textFilterResolver(columnQueryState: ColumnQueryState): TextColumnFilter | null {
        const rawValue = columnQueryState.filterValue;
        const parsedValue = Array.isArray(rawValue) ? rawValue[0] : rawValue;

        if (parsedValue == null) {
            return null;
        }

        return {
            filterType: 'text',
            filter: parsedValue ?? null,
            type: columnQueryState.filterOperator ?? 'contains'
        };
    }

    private numberFilterResolver(columnQueryState: ColumnQueryState): NumberColumnFilter | null {
        const rawValue = columnQueryState.filterValue;
        const parsedValue = Array.isArray(rawValue) ? Number(rawValue[0]) : Number(rawValue);

        if (Number.isNaN(parsedValue)) {
            return null;
        }

        return {
            filterType: 'number',
            filter: parsedValue,
            type: columnQueryState.filterOperator ?? 'equals'
        };
    }

    private dateFilterResolver(columnQueryState: ColumnQueryState): DateColumnFilter | null {
        const rawValue = columnQueryState.filterValue;
        const parsedValue = Array.isArray(rawValue) ? rawValue[0] : rawValue;

        if (parsedValue == null) {
            return null;
        }

        return {
            filterType: 'date',
            dateFrom: parsedValue ?? null,
            type: columnQueryState.filterOperator ?? 'equals'
        };
    }

    private setFilterResolver(columnQueryState: ColumnQueryState): SetColumnFilter | null {
        const rawValue = columnQueryState.filterValue ? columnQueryState.filterValue : [];
        const parsedValue = Array.isArray(rawValue) ? rawValue : [rawValue];

        return {
            filterType: 'set',
            values: parsedValue
        };
    }


    getColumnFiltersFromQueryParams(reportColumnDefinitions: ReportColumnDefinition[], queryColumnDefaults: Record<string, ColumnQueryState> | null): ColumnFilters | null  {
        if (queryColumnDefaults == null) {
            return null;
        }
        
        const columnDefinitionMap = new Map<ReportColumnKeys, ReportColumnDefinition>(
            reportColumnDefinitions.map(c => ([c.key as ReportColumnKeys, c]))
        );

        const filters: ColumnFilters = {};

        for (const columnKey in queryColumnDefaults) {
            const columnDef = columnDefinitionMap.get(columnKey as ReportColumnKeys);
            if (!columnDef) {
                continue;
            }

            /* Use the column definitions filter type if provided. Otherwise get it based on the Type of the column */
            const filterType = columnDef.filterType ?? COLUMN_TYPE_FILTER_MAP[columnDef.type];
            
            if (!filterType) {
                continue;
            }

            const resolver = this.FILTER_MODEL_RESOLVERS[filterType];

            if (!resolver) {
                continue;
            }

            const filter = resolver(queryColumnDefaults[columnKey]);
            if (filter) {
                filters[columnKey as ReportColumnKeys] = filter;    
            }
        }

        return Object.keys(filters).length > 0 ? filters : null;
    }

    getLayoutDefinitionFromGridState(gridState: GridState, baseColumnDefinitions: ReportColumnDefinition[]) {
        /* LayoutOptions */
        const layoutOptions = this.getLayoutOptionsFromGridState(gridState);        

        /* Columns */
        const columns = this.getColumnConfigsFromGridState(gridState, baseColumnDefinitions);

        const definition: LayoutDefinition = {
            layoutOptions: layoutOptions,
            columns: columns
        };

        return definition;
    }

    private getLayoutOptionsFromGridState(gridState: GridState): LayoutOptions {
        const layoutOptions: LayoutOptions = {
            pivotMode: gridState?.pivot?.pivotMode ?? false
        };

        return layoutOptions;
    }

    private getColumnConfigsFromGridState(gridState: GridState, baseColumnDefinitions: ReportColumnDefinition[]): ReportColumnConfig[] {
        const columnConfigs: ReportColumnConfig[] = [];

        const columnOrder = gridState.columnOrder?.orderedColIds ?? [];
        const columnSizingModel = gridState.columnSizing?.columnSizingModel ?? [];
        const hiddenColumns = gridState.columnVisibility?.hiddenColIds ?? [];
        const rowGroupColumns = gridState.rowGroup?.groupColIds ?? [];
        const pivotColumns = gridState.pivot?.pivotColIds ?? [];
        const sortModel: SortModelItem[] = gridState.sort?.sortModel ?? [];
        const leftPinnedColumns = gridState.columnPinning?.leftColIds ?? [];
        const rightPinnedColumns = gridState.columnPinning?.rightColIds ?? [];
        const aggregationModel: AggregationColumnState[] = gridState.aggregation?.aggregationModel ?? [];

        baseColumnDefinitions.forEach((col) => {

            const columnKey = col.key as ReportColumnKeys;
            const overrides: Partial<ReportColumnDefinition> = {};

            /* Exclude columns not in the Grid State */
            if (!columnOrder.includes(columnKey)) {
                return;
            }

            const orderIndex = columnOrder.findIndex(c => c === columnKey);
            overrides.order = orderIndex < 0 ? undefined : orderIndex;
            overrides.visible = !hiddenColumns.includes(columnKey);
            overrides.rowGroup = rowGroupColumns.includes(columnKey);
            overrides.pivot = pivotColumns.includes(columnKey);

            if (leftPinnedColumns.includes(columnKey)) {
                overrides.pinned = 'left';
            } 
            else if (rightPinnedColumns.includes(columnKey)) {
                overrides.pinned = 'right';
            }
            
            const sortIndex = sortModel.findIndex(s => s.colId === columnKey);
            if (sortIndex >= 0) {
                overrides.sortIndex = sortIndex;
                overrides.sortDirection = sortModel[sortIndex].sort;
            }

            const aggregationIndex = aggregationModel.findIndex(m => m.colId === columnKey);
            if (aggregationIndex >= 0 ) {
                overrides.aggFunc = aggregationModel[aggregationIndex].aggFunc;
            }

            const widthIndex = columnSizingModel.findIndex(m => m.colId === columnKey);
            if (widthIndex >= 0) {
                overrides.width = columnSizingModel[widthIndex].width ?? null;
            }

            columnConfigs.push({key: columnKey, overrides});
        });

        return columnConfigs;
    }   
}

type ResolverFn = (columnQueryState: ColumnQueryState) => ColumnFilter | null;
